"use client"

import { useFormContext } from "react-hook-form"
import { Check } from "lucide-react"
import { ConsultationFormSchema } from "../schema/schema"
import { ConsultationStepData } from "../types/type"


interface MedicationSelectionStepProps {
  data: ConsultationStepData
  updateFields: (fields: Partial<ConsultationFormSchema>) => void
}

export const MedicationSelectionStep = ({ data, updateFields }: MedicationSelectionStepProps) => {
  const { register, watch, setValue } = useFormContext<ConsultationFormSchema>()
  const selectedMedication = watch("medication")
  console.log("medicationSelectData", data, selectedMedication)

  const handleMedicationSelect = (medication: string) => {
    const medicationPrices: { [key: string]: number } = {
      Compounded_Semaglutide: 199,
      Compounded_Tirzepatide: 299,
      Zepbound: 50.00,
    };
    const cost = medicationPrices[medication] || 0;
    setValue("medication", { name: medication, cost }, { shouldValidate: true })
    updateFields({ medication: { name: medication, cost } })
  }

  // Medication details for the info panel
  const medicationDetails = {
    Compounded_Semaglutide: {
      title: "What You Need to Know about Compounded_Semaglutide",
      howItWorks: [
        "Mimics a natural hormone (GLP-1) that tells your brain you’re full. ",
        "Curbs appetite, lowers blood pressure, and supports steady weight loss over time.",
      ],
      howToTakeIt: [
        "Weekly at-home injections, sent directly to you.",
      ],
      supportVitamins: [
        "Vitamin B12 to keep your energy up and support your workouts.",
        "Vitamin B6 for nausea and appetite",
      ],
      bestFor: [
        "Lose 6–10% of your body weight in 3–6 months.",
      ],
      sideEffects: [
        "Abdominal discomfort or pain",
        "Reduced appetite (anorexia)",
        "Headache, fatigue, and dizziness",
      ],
      whyChoose: [
        "It’s affordable, easy to start, and great for first-time weight loss support.",
      ],
      investments: [
        "Lose 6–10% of your body weight in 3–6 months.",
      ],
    },
    Compounded_Tirzepatide: {
      title: "What You Need to Know about Compounded_Tirzepatide",
      howItWorks: [
        "Targets both GIP & GLP‑1 hormone receptors ",
        "Helps lower blood sugar, decrease appetite, and prevent fat accumulation",
      ],
      howToTakeIt: [
        "Weekly at-home injections, sent directly to you.",
      ],
      supportVitamins: [
        "Vitamin B12 to keep your energy up and support your workouts.",
        "Vitamin B6 for nausea and appetite",
      ],
      bestFor: [
        "Lose 15–19% of your weight in 3–6 months, faster than with semaglutide.",
      ],
      sideEffects: [
        "Abdominal discomfort or pain",
        "Reduced appetite (anorexia)",
        "Headache, fatigue, and dizziness",
      ],
      whyChoose: [
        "Produces greater reduction in body weight and blood glucose ",
        "Comes with vitamin B12 for energy and B6 for nausea",
      ],
      investments: [
        "Starts at $299/month with full medical supervision and personalized support"
      ],
    },
    Zepbound: {
      title: "What You Need to Know about Zepbound",
      howItWorks: [
        "Targets both GIP & GLP‑1 hormone receptors ",
        "Helps lower blood sugar, decrease appetite, and prevent fat accumulation",
        "FDA-approved, proven safe and effective for weight management and diabetes support.",
      ],
      howToTakeIt: [
        "Use prefilled weekly injection pens with easy dose dialing ",
      ],
      supportVitamins: [
        "No additional vitamins are provided with Zepbound , as it follows a standard formulation. If you’re interested in vitamin support, please consult your doctor for personalized recommendations.",
      ],
      bestFor: [
        "Lose 15–19% of your weight in 3–6 months, based on large clinical studies.",
      ],
      sideEffects: [
        "Nausea or vomiting, diarrhea, and constipation",
        "Abdominal discomfort or pain",
        "Reduced appetite (anorexia)",
        "Headache, fatigue, and dizziness",
      ],
      whyChoose: [
        "A trusted, FDA-approved option, widely covered by insurance.",
      ],
      investments: [
        "Starting at $499/month (manufacturer pricing)",
      ],
    },
  }

  const selectedDetails = selectedMedication
    ? medicationDetails[selectedMedication.name as keyof typeof medicationDetails]
    : null

  // Mapping for background colors based on medication
  const medicationBackgroundColors: { [key: string]: string } = {
    Compounded_Semaglutide: 'bg-[#ECFDF5]', // Corresponds to the budget friendly light green
    Compounded_Tirzepatide: 'bg-[#EFF6FF]', // Corresponds to the most popular light blue
    Zepbound: 'bg-[#FFF5E680]', // Corresponds to the premium light yellow
  };

  const selectedBackgroundColorClass = selectedMedication
    ? medicationBackgroundColors[selectedMedication.name]
    : 'bg-[#E6FAF2]'; // Default background if no medication is selected

  return (
    <div className="mx-auto mb-24 w-full sm:max-w-[524px] lg:max-w-[1000px]">
      <div className="mb-6 text-center mt-2 space-y-[6px]">
        <h1 className="text-2xl font-medium sm:text-3xl lg:text-4xl">Select a medication plan</h1>
        <p className="mt-2 text-sm text-[#4B5563] sm:text-base lg:text-lg">
          Select the medication that's right for your weight loss journey. All options are <br /> clinically proven and
          FDA-approved for safe, effective weight management.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Compounded_Semaglutide Option */}
        <div
          className={`cursor-pointer rounded-xl border-1 bg-white shadow-md p-4 transition-all ${
            selectedMedication?.name === "Compounded_Semaglutide" ? "border-black" : "border-[#E5E7EB] hover:border-[#D1D5DB]"
          }`}
          onClick={() => handleMedicationSelect("Compounded_Semaglutide")}
        >
          <div className="mb-3 inline-block rounded-md bg-[#E6FAF2] px-2 py-1 text-xs font-medium text-[#00C781]">
            BUDGET FRIENDLY
          </div>
          <h3 className="text-lg font-medium mb-1">Compounded Semaglutide</h3>
          <p className="text-sm text-[#4B5563] mb-3">Weekly injection for steady, effective weight management</p>
          <p className="text-[28px] text-[#1A202C] font-medium mb-4">$199/month</p>

          <div className="bg-[#E6FAF2] border-l-4 border-l-[#00C781] rounded-r-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-[#464B55]">AVERAGE WEIGHT LOSS</p>
              {selectedMedication?.name === "Compounded_Semaglutide" && (
                <div className="w-6 h-6 bg-[#4272DD] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <p className="text-lg font-medium text-[#1A202C]">8-12%</p>
            <p className="text-xs text-[#464B55]">Over 56 weeks in clinical studies</p>
          </div>

          <input
            type="radio"
            {...register("medication")}
            value="Compounded_Semaglutide"
            className="hidden"
            checked={selectedMedication?.name === "Compounded_Semaglutide"}
            onChange={() => {}}
          />
        </div>
        {selectedMedication?.name === "Compounded_Semaglutide" && selectedDetails && (
        <div className="mt-8 relative lg:hidden border-2 border-[#6C757D] rounded-xl">
          {/* Arrow pointing up */}
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 `}>
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-b-[12px] border-l-transparent border-r-transparent border-b-[#6C757D]"></div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] lg:p-6 p-4 bg-white">
            <h2 className="mb-4 text-xl font-medium text-center">{selectedDetails.title}</h2>

            <div className={`${selectedBackgroundColorClass} divide-y divide-[#0404041A] rounded-2xl`}>
            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How It Works:</h3>
              <ul className="space-y-1">
                {selectedDetails.howItWorks.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How You Should Take It:</h3>
              <ul className="space-y-1">
                {selectedDetails.howToTakeIt.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Support Vitamins:</h3>
              <ul className="space-y-1">
                {selectedDetails.supportVitamins.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Expected Results:</h3>
              <ul className="space-y-1">
                {selectedDetails.bestFor.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Common Side Effects:</h3>
              <ul className="space-y-1">
                {selectedDetails.sideEffects.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Why Choose:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-0 py-6 px-4">
              <h3 className="font-medium mb-2">Investments:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            </div>

          </div>
        </div>
      )}
        {/* Compounded_Tirzepatide Option */}
        <div
          className={`cursor-pointer rounded-xl border-1 bg-white shadow-md p-4 transition-all ${
            selectedMedication?.name === "Compounded_Tirzepatide" ? "border-black" : "border-[#E5E7EB] hover:border-[#D1D5DB]"
          }`}
          onClick={() => handleMedicationSelect("Compounded_Tirzepatide")}
        >
          <div className="mb-3 inline-block rounded-md bg-[#F0F5FF] px-2 py-1 text-xs font-medium text-[#2A6BFF]">
            MOST POPULAR
          </div>
          <h3 className="text-lg font-medium mb-1">Compounded Tirzepatide</h3>
          <p className="text-sm text-[#4B5563] mb-3">Dual-action weekly injection for maximum results</p>
          <p className="text-[28px] text-[#1A202C] font-medium mb-4">$299/month</p>

          <div className="bg-[#EFF6FF] border-l-4 border-l-[#4272DD] rounded-r-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-[#464B55]">AVERAGE WEIGHT LOSS</p>
              {selectedMedication?.name === "Compounded_Tirzepatide" && (
                <div className="w-6 h-6 bg-[#4272DD] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <p className="text-lg font-medium text-[#1A202C]">15-20%</p>
            <p className="text-xs text-[#464B55]">Over 72 weeks in clinical studies</p>
          </div>

          <input
            type="radio"
            {...register("medication")}
            value="Compounded_Tirzepatide"
            className="hidden"
            checked={selectedMedication?.name === "Compounded_Tirzepatide"}
            onChange={() => {}}
          />
        </div>
        {selectedMedication?.name === "Compounded_Tirzepatide" && selectedDetails && (
        <div className="mt-8 relative lg:hidden border-2 border-[#6C757D] rounded-xl">
          {/* Arrow pointing up */}
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 `}>
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-b-[12px] border-l-transparent border-r-transparent border-b-[#6C757D]"></div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] lg:p-6 p-4 bg-white">
            <h2 className="mb-4 text-xl font-medium text-center">{selectedDetails.title}</h2>

            <div className={`${selectedBackgroundColorClass} divide-y divide-[#0404041A] rounded-2xl`}>
            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How It Works:</h3>
              <ul className="space-y-1">
                {selectedDetails.howItWorks.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How You Should Take It:</h3>
              <ul className="space-y-1">
                {selectedDetails.howToTakeIt.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Support Vitamins:</h3>
              <ul className="space-y-1">
                {selectedDetails.supportVitamins.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Expected Results:</h3>
              <ul className="space-y-1">
                {selectedDetails.bestFor.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Common Side Effects:</h3>
              <ul className="space-y-1">
                {selectedDetails.sideEffects.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Why Choose:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-0 py-6 px-4">
              <h3 className="font-medium mb-2">Investments:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            </div>

          </div>
        </div>
      )}
        {/* Zepbound Option */}
        <div
          className={`cursor-pointer rounded-xl border-1 bg-white shadow-md p-4 transition-all ${
            selectedMedication?.name === "Zepbound" ? "border-black" : "border-[#E5E7EB] hover:border-[#D1D5DB]"
          }`}
          onClick={() => handleMedicationSelect("Zepbound")}
        >
          <div className="mb-3 inline-block rounded-md bg-[#FFEBEB] px-2 py-1 text-xs font-medium text-[#FFA726]">
            PREMIUM
          </div>
          <h3 className="text-lg font-medium mb-1">Zepbound (Brand Tirzepatide)</h3>
          <p className="text-sm text-[#4B5563] mb-3">Weekly injection for steady, effective weight management</p>
          <p className="text-[28px] text-[#1A202C] font-medium mb-4">$499/month</p>

          <div className="bg-[#FFF5E680] border-l-4 border-l-[#FFA726] rounded-r-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-[#464B55]">AVERAGE WEIGHT LOSS</p>
              {selectedMedication?.name === "Zepbound" && (
                <div className="w-6 h-6 bg-[#4272DD] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <p className="text-lg font-medium text-[#1A202C]">12-15%</p>
            <p className="text-xs text-[#464B55]">Over 68 weeks in clinical studies</p>
          </div>

          <input
            type="radio"
            {...register("medication")}
            value="Zepbound"
            className="hidden"
            checked={selectedMedication?.name === "Zepbound"}
            onChange={() => {}}
          />
        </div>
        {selectedMedication?.name === "Zepbound" && selectedDetails && (
        <div className="mt-8 relative lg:hidden border-2 border-[#6C757D] rounded-xl">
          {/* Arrow pointing up */}
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 `}>
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-b-[12px] border-l-transparent border-r-transparent border-b-[#6C757D]"></div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] lg:p-6 p-4 bg-white">
            <h2 className="mb-4 text-xl font-medium text-center">{selectedDetails.title}</h2>

            <div className={`${selectedBackgroundColorClass} divide-y divide-[#0404041A] rounded-2xl`}>
            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How It Works:</h3>
              <ul className="space-y-1">
                {selectedDetails.howItWorks.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How You Should Take It:</h3>
              <ul className="space-y-1">
                {selectedDetails.howToTakeIt.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Support Vitamins:</h3>
              <ul className="space-y-1">
                {selectedDetails.supportVitamins.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Expected Results:</h3>
              <ul className="space-y-1">
                {selectedDetails.bestFor.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Common Side Effects:</h3>
              <ul className="space-y-1">
                {selectedDetails.sideEffects.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Why Choose:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-0 py-6 px-4">
              <h3 className="font-medium mb-2">Investments:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            </div>

          </div>
        </div>
      )}
      </div>

      {/* Details Panel - Only show when a medication is selected */}
      {selectedMedication && selectedDetails && (
        <div className="mt-8 lg:block relative hidden border-2 border-[#6C757D] rounded-xl">
          {/* Arrow pointing up */}
          <div className={`absolute -top-3 ${selectedMedication?.name === "Compounded_Semaglutide" ? "left-[calc(16.66%)]" : selectedMedication?.name === "Zepbound" ? "left-[calc(83.33%)]" : "left-1/2 transform -translate-x-1/2"}`}>
            <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-b-[12px] border-l-transparent border-r-transparent border-b-[#6C757D]"></div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] lg:p-6 p-4 bg-white">
            <h2 className="mb-4 text-xl font-medium text-center">{selectedDetails.title}</h2>

            <div className={`${selectedBackgroundColorClass} divide-y divide-[#0404041A] rounded-2xl`}>
            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How It Works:</h3>
              <ul className="space-y-1">
                {selectedDetails.howItWorks.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">How You Should Take It:</h3>
              <ul className="space-y-1">
                {selectedDetails.howToTakeIt.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Support Vitamins:</h3>
              <ul className="space-y-1">
                {selectedDetails.supportVitamins.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Expected Results:</h3>
              <ul className="space-y-1">
                {selectedDetails.bestFor.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Common Side Effects:</h3>
              <ul className="space-y-1">
                {selectedDetails.sideEffects.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-4 py-6 px-4">
              <h3 className="font-medium mb-2">Why Choose:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-0 py-6 px-4">
              <h3 className="font-medium mb-2">Investments:</h3>
              <ul className="space-y-1">
                {selectedDetails.whyChoose.map((item, index) => (
                  <li key={index} className="text-sm text-[#4B5563] flex items-start">
                    <span className="mr-2 mt-1.5 w-1 h-1 bg-[#4B5563] rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
